
import { defineComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import ResizeObserver from '@/components/ResizeObserver.vue'
import TutorialModal from '@/components/TutorialModal.vue'
import MessageModal from '@/components/common/MessageModal.vue'
import UserToolbar from '@/components/UserToolbar.vue'
import { AuthActionTypes } from '@/store/modules/auth/interfaces/action-types'
import { LinksActionType } from '@/store/modules/links/interfaces'
import { setI18nLanguage } from '@/i18n'
import Error401Page from './pages/Error401Page.vue'
import { constants } from '@/constants'
import BeforeLoginLayout from '@/components/layout/BeforeLoginLayout.vue'
import { LanguagesType } from '@/types'
import { HelpPagesActionType } from '@/store/modules/help-pages/interfaces'
import AppGlobalModal from '@/components/app/AppGlobalModal.vue'
import ModalComponent from './components/ModalComponent.vue'
import { ArticlesMutationType } from './store/modules/articles/interfaces/mutation-type'
import { FilesMutationType } from './store/modules/files/interfaces/mutation-type'
import { AuthMutationTypes } from '@/store/modules/auth/interfaces/mutation-types'
import { RolePermissionDetails } from './store/modules/users/interfaces/IUser'
import { IAuthState } from './store/modules/auth/IAuthState'
import { IFetchSearchResponse } from '@/store/modules/main/intefaces/fetchSearch/IFetchSearchResponse'
import { IPortalHardCodeLink } from './store/modules/links/interfaces/IPortalHardCodeLink'
import { getValueByLabel } from './utils/permissions'
import AppLayout from '@/components/layout/AppLayout.vue'
import { SupportActionType } from '@/store/modules/support/interfaces/action-type'
import modals from '@/utils/modals'
import LoginErrorModal from '@/components/modals/LoginErrorModal.vue'
import { getUnseenHelpPages } from './utils/newCapabilities'
import { CommonMutationType } from './store/modules/common/mutation-type'
import AlertModal from './components/AlertModal.vue'
import refreshAfterChangeLanguage from '@/mixins/refreshAfterChangeLanguage'

export default defineComponent({
  name: 'App',

  mixins: [refreshAfterChangeLanguage],
  components: {
    TutorialModal,
    Error401Page,
    MessageModal,
    UserToolbar,
    BeforeLoginLayout,
    ModalComponent,
    AppGlobalModal,
    ResizeObserver,
    AppLayout,
    AlertModal
  },
  data: () => ({
    selectedLanguage: 'pl',
    showTutorialModal: false,
    gigICode: '',
    gigIvToken: '',
    isPortalChrome: true,
    theme: constants.application.theme,
    isLoaded: false,
    isMounted: false,
    openModal: false,
    message: '',
    subMessage: '',
    searchValue: '',
    searchValues: [] as IFetchSearchResponse[],
    isErrorModal: true,
    copyRightMsg: `© Copyright Asahi CE & Europe Services s.r.o., ${new Date().getFullYear()}`,
    activeLanguage: (localStorage.getItem('lang')) as LanguagesType,
    clientWidth: 0,
    timeoutId: 0,
    isTimerSetup: false,
    alreadyOpenedTutorialModal: false,
    content: {} as any,
    showAlert: false,
    lastAlertsRequestKey: null as string | null
  }),

  computed: {
    ...mapState('main', ['pageNotFound']),
    ...mapState('basket', ['id', 'items', 'progressBarMsg']),
    ...mapState('catalog', ['wishlist']),
    ...mapGetters('main', ['getPermissions']),
    isPermissionSchemaExist () {
      return (this.user?.permissionSchema && this.user?.permissionSchema.length)
    },
    isMultipleTypeCustomer () {
      return this.$store.state.common.multipleTypeCustomer
    },
    isOntradeCustomerType () {
      return this.user?.ontrade && !this.user?.distributor
    },
    isDistributorCustomerType () {
      return this.user?.distributor && !this.user?.ontrade
    },
    menu () {
      const menuLinks = this.menuLinks
      const linksFromMenuLinks = Array.isArray(menuLinks) ? [...menuLinks] : []
      // const userCountry = this.user.country
      return [
        ...this.checkPermission('Catalogue', {
          label: 'Vytvořit objednávku',
          href: '/catalog/products',
          name: 'createAnOrder',
          isActive: true,
          icon: 'shopping-bag'
        }),
        ...this.checkPermission('Catalogue', {
          label: 'Košík',
          name: 'cart',
          ...(this.clientWidth >= 1000
            ? {
                href: '',
                precart: true
              }
            : { href: '/cart' }),
          icon: 'shopping-cart'
        }),
        ...this.checkPermission('Catalogue', {
          label: 'Moje oblíbené',
          name: 'favoritesMenu',
          href: '/catalog/favorites',
          icon: 'heart'
        }),
        {
          // EMPTY MENU PARENT
          isActive: true,
          children: [
            ...this.checkPermission('Home Page', {
              label: 'Hlavní stránka',
              href: '/',
              name: 'home',
              isActive: false,
              icon: 'home-alt'
            }),
            ...this.checkPermission('My Profile', {
              label: 'Můj profil',
              href: '/my-profile',
              name: 'profile',
              isActive: false,
              icon: 'user'
            }),
            ...this.checkPermission('Catalogue', {
              label: 'Katalog',
              name: 'catalog',
              isActive: false,
              icon: 'beer',
              children: [
                {
                  label: 'Produkty',
                  href: '/catalog/products',
                  name: 'products',
                  isActive: false
                },
                {
                  label: 'Moje oblíbené',
                  href: '/catalog/favorites',
                  name: 'favorites',
                  isActive: false
                },
                {
                  label: 'Uložené objednávky',
                  href: '/orders/saved',
                  name: 'savedOrders',
                  isActive: false
                },
                ...this.checkPermission('Product Recommendation', {
                  label: 'Uložené objednávky',
                  href: '/catalog/suggested-cart',
                  name: 'suggestedCart',
                  isActive: false
                })
              ]
            }),
            ...this.checkPermission('My Orders', {
              label: 'Objednávky',
              name: 'orders',
              href: '/orders/history',
              isActive: false,
              icon: 'shopping-bag'
            }),
            ...this.checkPermission('My Documents', {
              label: 'Moje dokumenty',
              href: '/my-documents',
              name: 'myDocuments',
              isActive: false,
              icon: 'newspaper'
            }, this.user.country === 'CZE'),
            ...this.checkPermission('Reporting', {
              label: 'Přehled odběrů',
              href: '/reporting',
              name: 'reporting',
              isActive: false,
              icon: 'newspaper'
            }),
            ...this.checkPermission('Contracts', {
              label: 'Přehled smluv',
              href: '/contracts',
              name: 'contracts',
              isActive: false,
              icon: 'file-contract'
            }),
            ...this.checkPermission('Support', {
              label: 'Podpora',
              href: '/support/faq',
              name: 'support',
              isActive: false,
              icon: 'question-circle',
              children: [
                {
                  label: 'Otázky a odpovědi',
                  href: '/support/faq',
                  name: 'faq',
                  isActive: false
                },
                {
                  label: 'O portálu',
                  href: '/support/knowledge-base',
                  name: 'knowledgeBase',
                  isActive: false
                },
                {
                  label: 'Kontaktujte nás',
                  href: '/support/tickets',
                  name: 'supportTickets',
                  isActive: false
                }
              ]
            }),
            ...this.checkPermission('Marketing Materials', {
              label: 'Materiály pro Vás',
              href: '/marketing-materials',
              name: 'marketingMaterials',
              isActive: false,
              icon: 'newspaper'
            }),
            ...this.checkPermission('Training & Learning', {
              label: 'Formazione',
              name: 'trainingLearning',
              href: '/training',
              icon: 'heart'
            }),
            ...this.checkPermission('News & Events', {
              label: 'Novinky a články',
              href: '/news',
              name: 'news',
              isActive: false,
              icon: 'newspaper'
            }),
            // TODO: this roles probably don't exist for CZ/SK market. Suggestion to comment the code for while, just to be sure. Then remove
            // ...this.checkPermission(
            //   'Empties Management',
            //   {
            //     label: 'Portale Materiale Circolante',
            //     href: constants.saml.empties,
            //     name: 'emptiesManagement',
            //     isActive: false,
            //     icon: 'external-link-alt'
            //   },
            //   this.isOnPremiseEmptiesUser || this.isOnPremiseFranchiseEmptiesUser
            // ),
            ...this.checkPermission(
              'Academy',
              {
                label: 'Akademie První u zdroje',
                href: this.user.country === 'SVK' ? this.getLinkByName('skELearning').url : this.getLinkByName('eLearning').url,
                name: this.user.country === 'SVK' ? 'skELearning' : 'eLearning',
                isActive: false,
                icon: 'school'
              },
              this.isPOSUser || this.isWHSLUser || this.isOutletUser
            ),
            ...this.checkPermission(
              'Pub Menu',
              {
                label: 'Prazdroj menu',
                href: this.user.country === 'SVK' ? this.getLinkByName('skPubMenu').url : this.getLinkByName('pubMenu').url,
                name: this.user.country === 'SVK' ? 'skPubMenu' : 'pubMenu',
                isActive: false,
                icon: 'book-open'
              },
              this.isPOSUser || this.isOutletUser
            ),
            ...linksFromMenuLinks
          ]
        }
      ]
    },

    newMenu () {
      return [
        ...this.checkPermission('Catalogue', {
          label: 'Orders',
          name: 'order',
          isActive: false,
          icon: 'beer',
          children: [

            {
              label: 'Order History',
              name: 'orders',
              href: '/orders/history',
              isActive: false,
              icon: 'shopping-bag'
            },
            {
              label: 'Saved Orders',
              href: '/orders/saved',
              name: 'savedOrders',
              isActive: false
            },
            {
              label: 'Pending Orders',
              href: '/catalog/pending',
              name: 'pendingOrders',
              isActive: false
            }
          ]
        }),
        ...this.checkPermission('Catalogue', {
          label: 'More',
          name: 'order',
          isActive: false,
          icon: 'beer',
          children: [
            {
              label: 'Pub Menu',
              href: this.user.country === 'SVK' ? this.getLinkByName('skPubMenu').url : this.getLinkByName('pubMenu').url,
              name: this.user.country === 'SVK' ? 'skPubMenu' : 'pubMenu',
              isActive: false,
              icon: 'book-open'
            },
            {
              label: 'Marketing material',
              href: '/marketing-materials',
              name: 'marketingMaterials',
              isActive: false,
              icon: 'newspaper'
            },
            {

              label: 'Academy',
              href: this.user.country === 'SVK' ? this.getLinkByName('skELearning').url : this.getLinkByName('eLearning').url,
              name: this.user.country === 'SVK' ? 'skELearning' : 'eLearning',
              isActive: false,
              icon: 'school'

            }
          ]
        }),
        ...this.checkPermission('Support', {
          label: 'Support',
          href: '/support/faq',
          name: 'support',
          isActive: false,
          icon: 'question-circle',
          children: [
            {
              label: 'FAQ',
              href: '/support/faq',
              name: 'faq',
              isActive: false
            },
            {
              label: 'Knowledge',
              href: '/support/knowledge-base',
              name: 'knowledgeBase',
              isActive: false
            },
            {
              label: 'Other Requests',
              href: '/support/other-requests',
              name: 'otherRequests',
              isActive: false
            },
            {
              label: 'Contacts',
              href: '/support/contacts',
              name: 'contacts',
              isActive: false
            }
          ]
        }),
        ...this.checkPermission('Catalogue', {
          label: 'Create an Order',
          href: '/catalog/products',
          name: 'createAnOrder',
          isActive: true,
          icon: 'shopping-bag',
          children: [
            {
              label: 'Products',
              href: '/catalog/products',
              name: 'products',
              isActive: false
            },
            {
              label: 'Suggested Cart',
              href: '/catalog/suggested-cart',
              name: 'suggestedCart',
              isActive: false
            }
          ]
        })
      ]
    },
    rightMenu () {
      return [
        ...this.checkPermission('My Profile', {
          label: 'My profile',
          href: '/my-profile',
          name: 'profile',
          isActive: false

        }),
        ...this.checkPermission('My Documents', {
          label: 'My Documents',
          href: '/my-documents',
          name: 'myDocuments',
          isActive: false,
          icon: 'newspaper'
        }),
        ...this.checkPermission('Reporting', {
          label: 'Reporting',
          href: '/reporting',
          name: 'reporting',
          isActive: false,
          icon: 'newspaper'
        }),
        ...this.checkPermission('Contracts', {
          label: 'Contracts',
          href: '/contracts',
          name: 'contracts',
          isActive: false,
          icon: 'file-contract'
        })
      ]
    },
    footerLinksSocial () {
      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.type.toLowerCase() === 'social media')
        .map((data) => ({
          label: data.name,
          link: data.url,
          target: '_blank'
        }))
      return aboutUsLinks
    },
    portalHardCodeLink (): IPortalHardCodeLink[] {
      return this.$store.state.links.portalHardCodeLink?.results ?? []
    },
    total () {
      return this.$store.state.links.portalHardCodeLink.total
    },
    portalPageMenuLink () {
      return this.$store.state.links.portalPageMenuLink?.items ?? []
    },
    limit () {
      return this.$store.state.links.portalHardCodeLink.limit
    },

    footerLinks () {
      let aboutUsLinks

      if (this.user.country === 'SVK') {
        aboutUsLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'about us sk' || data.type.toLowerCase() === 'about us')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      } else {
        aboutUsLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'about us cz' || data.type.toLowerCase() === 'about us')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      }

      return [
        {
          title: 'about'
        },
        ...aboutUsLinks
      ]
    },
    footerLinksBrands () {
      let brandLinks

      if (this.user.country === 'SVK') {
        brandLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'mobile brands sk' || data.type.toLowerCase() === 'mobile brands')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      } else {
        brandLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'mobile brands cz' || data.type.toLowerCase() === 'mobile brands')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      }
      return [
        {
          title: 'brands'
        },
        ...brandLinks
      ]
    },
    footerLinksNew () {
      let aboutUsLinks

      if (this.user.country === 'SVK') {
        aboutUsLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'about us sk' || data.type.toLowerCase() === 'about us')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      } else {
        aboutUsLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'about us cz' || data.type.toLowerCase() === 'about us')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      }

      let brandLinks

      if (this.user.country === 'SVK') {
        brandLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'mobile brands sk' || data.type.toLowerCase() === 'mobile brands')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      } else {
        brandLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'mobile brands cz' || data.type.toLowerCase() === 'mobile brands')
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      }
      return [
        {
          title: 'brands'
        },
        {
          title: 'about'
        },
        ...aboutUsLinks,
        ...brandLinks
      ]
    },
    footerLinksMobile () {
      return [
        ...this.footerLinksBrands, ...this.footerLinks
      ]
    },

    linksStore () {
      return this.$store.state.links
    },
    queries () {
      return this.$store.state.catalog.queries
    },
    showSearchBar () {
      return this.checkPermission('Catalogue', {}).length !== 0
    },
    catalogPermission () {
      return this.checkPermission('Catalogue', {}).length !== 0
    },

    languages () {
      return ['pl', 'en', this.activeLanguage].filter((x, index, self) => x != null && self.indexOf(x) === index)
    },

    menuLinks () {
      const links = this.linksStore?.links?.map((link, index) => {
        return {
          label: link?.title,
          href: link?.url,
          name: 'custom-link' + (index + 1),
          isActive: false,
          icon: 'external-link-alt',
          isOther: true,
          children: []
        }
      })
      return links
    },

    user () {
      return this.$store.state.auth.user
    },

    isLoggedIn () {
      return this.user.isLoggedIn // && !!this.user.userValidation
    },
    notAuthorized () {
      return this.$route.meta?.requireAuthentication && !this.isLoggedIn
    },
    showFooter (): boolean {
      return !this.$route.meta?.hideFooter
    },
    cartItems () {
      return this.items?.length
    },
    isMagicLink () {
      return this.$route.query.gig_actions === 'magicLink'
    },
    favoritesItems () {
      return this.wishlist?.items_v2?.shipto_item_count
    },
    isPOSUser () {
      return this.user?.permissionSchema?.some((company) => company.rpDetails.some((rp) => rp.customerType === 'POS'))
    },
    isWHSLUser () {
      return this.user?.permissionSchema?.some((company) => company.rpDetails?.some((rp) => rp.customerType === 'WHSL'))
    },
    isOutletUser () {
      return this.user?.permissionSchema?.some((company) =>
        company.rpDetails?.some((rp) => rp.customerType === 'Outlets')
      )
    },
    allPermission () {
      return this.user.allAvailablePermission
    },
    helpPagesStore () {
      return this.$store.state.helpPages
    },
    customerType (): RolePermissionDetails | undefined {
      if (this.user && this.user?.permissionSchema?.length > 0) {
        return this.user?.permissionSchema[0]?.rpDetails?.find((item: RolePermissionDetails) => item.customerType)
      }
      return undefined
    },
    isPOSAcademyUser () {
      return this.user.permissionSchema?.every(permission => permission.rpDetails.every(
        roleItem =>
          roleItem.rolename === 'POS Academy' ||
          roleItem.roleid === 'f5ab4859-9197-47e5-bc9e-097283c35d94'
      ))
    },
    defaultShipTo () {
      return this.$store.state.auth.defaultShipTo
    }
  },
  watch: {
    isFullLoaded: {
      handler (value) {
        if (value === true) {
          const impUsr = this.$route.query.imp_usr
          if (impUsr) {
            sessionStorage.setItem('imp_usr', impUsr.toString())
          }
          this.$navigate('/')
        }
      },
      immediate: true
    },
    isOntradeCustomerType: {
      handler (value) {
        if (value === true) {
          this.fetchLinks()
          this.fetchPortalPageMenuLink()
        }
        this.fetchPortalHardCodeLink()
      }
    },
    isDistributorCustomerType: {
      handler (value) {
        if (value === true) {
          this.fetchLinks()
          this.fetchPortalPageMenuLink()
        }
        this.fetchPortalHardCodeLink()
      }
    },
    isMultipleTypeCustomer: {
      handler (value) {
        if (value === true) {
          this.onCustomerSelect()
          this.fetchPortalPageMenuLink()
        }
      },
      immediate: true
    },
    isLoggedIn: {
      handler (value) {
        if (value === true && localStorage.getItem('customertype')) {
          this.fetchLinks()

          this.fetchPortalPageMenuLink()
        }
        this.fetchPortalHardCodeLink()
        if (value === true && localStorage.getItem('customertype')) {
          this.dispatchAlerts().then(() => this.getAlertContent())
        }
      }
    },
    activeLanguage: {
      handler (value) {
        this.setMagentoStore(value)
        if (this.isLoggedIn && localStorage.getItem('customertype')) {
          this.fetchLinks()
        }
        this.fetchPortalHardCodeLink()
      },
      immediate: true
    },
    customerType (newVal) {
      if (newVal) {
        this.fetchPortalPageMenuLink()
        this.dispatchAlerts().then(() => this.getAlertContent())
      }
    },

    $route () {
      this.initFromRoute()
      this.getAlertContent()
    },

    progressBarMsg (value, oldValue) {
      if (oldValue !== value) {
        this.$store.commit('basket/SET_PROGRESS_BAR', value)
        return value
      }
    }
  },

  created () {
    this.initFromRoute()
  },

  async mounted () {
    if (this.$route.query.selectedLang) {
      localStorage.setItem('lang', this.$route.query.selectedLang.toString())
      this.$router.replace({
        query: {
          ...this.$route.query,
          selectedLang: undefined
        }
      })
      const multipleCustomerType = localStorage.getItem('multipleCustomerType')
      if (multipleCustomerType && multipleCustomerType !== '') {
        localStorage.removeItem('multipleCustomerType')
      }
    } else {
      if (!localStorage.getItem('lang')) {
        localStorage.setItem('lang', 'pl')
      }
    }

    this.checkGigyaReady()
    this.isMounted = true
  },

  methods: {

    getBGClass () {
      if (this.$route.name === 'brands' || this.$route.name === 'newbrands') {
        return 'greyBackground'
      } else {
        return ''
      }
    },
    checkGigyaReady () {
      if (!window.gigya?.isReady) {
        setTimeout(() => this.checkGigyaReady(), 100)
        return
      }

      window.gigya.socialize.addEventHandlers({
        onLogin: async (res: IAuthState) => {
          localStorage.setItem('isSubmitAgeConfirmed', 'true')
          await this.updateUserInfo(res)
          sessionStorage.setItem('getUserInfoResponse', JSON.stringify(res))
          this.$store.commit('SET_AUTH_USER', res)
          sessionStorage.setItem('uid', res.UID)
          await this.onLoginSessionExist(true)
        }
      })

      const runSessionCheck = () => {
        window.gigya.hasSession().then(this.checkSession)
      }

      if (this.isMagicLink) {
        requestAnimationFrame(() => {
          setTimeout(runSessionCheck, 300)
        })
      } else {
        runSessionCheck()
      }
    },
    async getUserDetails (res : any) {
      const multipleCustomerType = localStorage.getItem('multipleCustomerType') || ''
      if (res.data?.distributor && res.data?.ontrade && multipleCustomerType === '' && this.user.userValidation) {
        this.$navigate('/select-login-view')
      } else {
        await this.callOnLoginOnly(res)
      }
    },

    async onLoginSessionExist (sessionExist: boolean) {
      if (sessionExist) {
        window.gigya.accounts.getAccountInfo({ callback: this.getUserDetails })
      }
    },

    async onCustomerSelect () {
      this.$store.commit(CommonMutationType.REMOVE_MULTIPLE_CUSTOMER)
      window.gigya.accounts.getAccountInfo({ callback: this.callOnLoginOnly })
    },

    async callOnLoginOnly (res : any) {
      const userInfoResponse = sessionStorage.getItem('getUserInfoResponse')
      const resp = userInfoResponse ? JSON.parse(userInfoResponse) : {}

      await this.updateUserInfo(resp)
      if (this.user?.userValidation) {
        this.$store.commit(CommonMutationType.INCREMENT_LOADING)
        await this.$store.dispatch('basket/REMOVE_ALL_ITEMS', { cart_id: this.id }, { root: true })
        await this.$store.dispatch('basket/CREATE_EMPTY_CART', { root: true })
        await this.$store.dispatch('basket/EMPTY_FREE_BEER', { cart_id: this.id }, { root: true })
        await this.$store.commit('catalog/SET_FREE_BEER_PRODUCT', [], { root: true })
        this.$store.commit(CommonMutationType.DECREMENT_LOADING)
        this.$navigate('/')
      }
    },
    getAlertContent () {
      const availableAlert = this.$store.state.helpPages.alerts as any
      if (availableAlert?.length > 0) {
        const currentLocation = this.$route.name as string
        const alertForCurrentPage = availableAlert.find((item: any) => {
          const availablePages = item.portalSections?.map((section: any) => section.name) || []
          return availablePages.includes(currentLocation)
        })
        const storedAlerts = localStorage.getItem('alerts')
        const alreadyShownAlerts =
      storedAlerts && JSON.parse(storedAlerts || '[]')
        const alreadyShownForCurrentPage = alreadyShownAlerts?.find(
          (alert: { id: string; page: string; confirmationNeeded: boolean }) => {
            return alert.id === alertForCurrentPage?.id && alert.page === currentLocation
          }
        )
        if (alertForCurrentPage && !alreadyShownForCurrentPage) {
          this.content = alertForCurrentPage
          this.showAlert = true
        } else {
          this.content = null
          this.showAlert = false
        }
      }
    },
    async dispatchAlerts (force = false) {
      const isPublic = !this.isLoggedIn
      const multipleCustomerType = localStorage.getItem('multipleCustomerType')
      const isCustomerTypeUnresolved = !isPublic && (
        (!!this.user?.ontrade && !!this.user?.distributor && !multipleCustomerType) ||
        !(this.customerType?.customerType || localStorage.getItem('customertype'))
      )
      if (isCustomerTypeUnresolved) {
        return
      }
      const customertype = this.customerType?.customerType || localStorage.getItem('customertype') || ''
      const uid = sessionStorage.getItem('uid')
      const requestKey = `${isPublic}|${customertype}|${this.activeLanguage}`
      if (!force && this.lastAlertsRequestKey === requestKey) {
        return
      }
      this.lastAlertsRequestKey = requestKey
      await this.$store.dispatch(
        HelpPagesActionType.FETCH_ALERTS,
        {
          public: isPublic,
          customertype,
          uid
        }
      )
    },
    async refresh () {
      await this.dispatchAlerts(true)
    },

    initFromRoute () {
      this.isPortalChrome = !this.$route.meta?.noPortalChrome
      const urlParams = new URLSearchParams(window.location.search)
      this.gigICode = urlParams.get('gig_i_code') || ''
      this.gigIvToken = urlParams.get('gig_i_vToken') || ''
    },
    checkPermission (permission: string, value: Record<string, any>, additionalPremmision = true) {
      const selectedCustomerType = localStorage.getItem('customertype') || ''
      if (this.customerType && selectedCustomerType === '') {
        localStorage.setItem('customertype', this.customerType?.customerType)
      }

      return this.allPermission?.includes(permission) &&
      additionalPremmision &&
      getValueByLabel(this.getPermissions(), permission)
        ? [value]
        : []
    },
    async dispatchHelpPages () {
      const payload = {
        CustomerType: localStorage.getItem('customertype') || '',
        uid: sessionStorage.getItem('uid')
      }

      await this.$store.dispatch(
        HelpPagesActionType.FETCH_ALL_HELP_PAGES,
        payload
      )
    },
    async openTutorialModal () {
      await modals.showModal({
        component: TutorialModal,
        backdropDismiss: this.user?.showTutorial !== 3,
        showCloseButton: false,
        isTutorialModal: true
      })
    },
    closeTutorialModal () {
      this.showTutorialModal = false
      this.alreadyOpenedTutorialModal = false
      this.showAlert = false
    },
    menuNavigate (item: CustomEvent) {
      if (
        item.detail?.name === 'eLearning' ||
        item.detail?.name === 'skELearning' ||
        item.detail?.name === 'skPubMenu' ||
        item.detail?.name === 'pubMenu'
      ) {
        window.gigya.fidm.saml.initSSO({
          spName: item.detail?.name,
          redirectURL: item.detail.href
        })
        return
      } else if (/^https?:\/\//i.test(item.detail.href) || /^https?:\/\//i.test(item.detail) || /^\/files\/\S*\w+\.\w+/i.test(item.detail)) {
        const link = item.detail.href ? item.detail.href : item.detail
        window.open(link, '_blank')
        return
      }

      // Default navigation
      this.$navigate(item)
    },

    changeLanguage (langCode: LanguagesType) {
      this.activeLanguage = langCode
      setI18nLanguage(langCode)
    },

    fetchLinks () {
      const payload = this.user?.isLoggedIn
        ? {
            CustomerType: localStorage.getItem('customertype') || '',
            uid: sessionStorage.getItem('uid')
          }
        : {}

      this.$store.dispatch(
        LinksActionType.FETCH_ALL_LINKS,
        payload
      )
    },
    fetchPortalHardCodeLink () {
      const payload = {
        CustomerType: localStorage.getItem('customertype') || '',
        uid: sessionStorage.getItem('uid')
      }

      this.$store.dispatch(
        LinksActionType.FETCH_PORTAL_HARD_CODE_LINK,
        payload
      )
    },
    fetchPortalPageMenuLink () {
      const customerType = this.customerType?.customerType

      if (customerType) {
        const payload = {
          CustomerType: customerType,
          ...(this.user?.isLoggedIn && {
            uid: sessionStorage.getItem('uid')
          })
        }

        this.$store.dispatch(
          LinksActionType.FETCH_PAGE_MENU_LINK,
          payload
        )
      } else {
        console.warn('customerType not available yet')
      }
    },

    getLinkByName (name: string) {
      const linkObj = this.portalHardCodeLink.find((link) => link.name === name)
      return linkObj ? { url: linkObj.url, label: linkObj.label } : { url: '', label: '' }
    },

    getShipToIds (data : any) {
      const selectedCustomerType = localStorage.getItem('multipleCustomerType') || ''
      if (selectedCustomerType !== '') {
        return data.filter((item: any) => item.rpDetails.some((role: any) => role.customerType === selectedCustomerType)).map((item: any) => item.shipToId).join(',')
      } else {
        return data?.map((item: any) => item.shipToId).join(',')
      }
    },
    /**
     * Method to update logged in user info into vuex store
     * @param res
     */
    async updateUserInfo (res: IAuthState) {
      sessionStorage.setItem('uid', res.UID) // This is for Google Analytics
      sessionStorage.setItem('getUserInfoResponse', JSON.stringify(res))
      this.$store.commit('SET_AUTH_USER', res)
      if (this.isLoggedIn) {
        this.setupTimers()
      }
      await this.$store.dispatch(AuthActionTypes.GET_USER_INFO, res.UID).then(async () => {
        if (sessionStorage.getItem('phoneLogin') === 'true' && !this.user.userValidation) {
          this.$navigate('/verification-phone')
          return
        } else {
          if (!this.user.userValidation && !this.$route.meta?.isUserVerification) {
            this.userLogout()
            return
          } else if (this.$route.name !== 'userVerification' && this.$route.name !== 'userRevalidate') {
            const multipleCustomerType = localStorage.getItem('multipleCustomerType') || ''

            if (this.user?.ontrade && this.user?.distributor && multipleCustomerType === '') {
              this.$navigate('/select-login-view')
            } else {
              const shipToIds = this.getShipToIds(this.user?.permissionSchema)
              await this.$store.dispatch('catalog/FETCH_SHIP_TO_ADDRESS', { id: shipToIds }, { root: true })
            }
          }
        }

        this.setMagentoStore(this.activeLanguage)

        if (!(localStorage.getItem('emptyFreeBeer') === 'true')) {
          if (!this.id || this.id === '') {
            await this.$store.dispatch('basket/FETCH_BASKET_ID', {}, { root: true })
          }
        }

        // await this.$store.dispatch('GET_DEFAULT_SHIP_TO', {}, { root: true })
        this.fetchLinks()
      })
      this.dispatchAlerts().then(() => this.getAlertContent())

      if (!!this.user.userValidation && !this.$route.meta?.isUserVerification) {
        await this.checkPOSAcademyUser()
      }

      if (
        (this.user?.showTutorial > 0 && !this.alreadyOpenedTutorialModal) &&
      (this.$route.name !== 'userVerification' && this.$route.name !== 'userVerificationPhone' && this.$route.name !== 'selectloginview')
      ) {
        this.openTutorialModal()
        this.alreadyOpenedTutorialModal = true
      }

      if (localStorage.getItem('customertype') && localStorage.getItem('shippingMethod')) {
        this.$store.dispatch('main/FETCH_ORDERABLE_UNITS_CONFIG',
          {
            customerType: localStorage.getItem('customertype'),
            orderType: localStorage.getItem('shippingMethod')
          }, { root: true })
      }
    },
    checkPOSAcademyUser () {
      if (this.isPOSAcademyUser) {
        window.gigya.fidm.saml.initSSO({
          spName: 'eLearning',
          redirectURL: this.getLinkByName('eLearning').url
        })
      }
    },
    /**
     * Method to initiate logout action
     * on click action of logout button
     */
    userLogout () {
      this.selectedLanguage = localStorage.getItem('lang') || 'pl'
      if (!this.isPermissionSchemaExist) {
        modals.showModal({
          component: LoginErrorModal,
          props: {
            message: this.$t('Error with your account. Please contact the customer service')
          }
        })
      }
      sessionStorage.removeItem('uid')
      sessionStorage.removeItem('imp_usr')
      const params = {
        UID: this.user.UID,
        callback: this.handleLogout
      }
      window.gigya.accounts.logout(params)
    },
    /**
     * Method to perform action after user logs out
     * Navigate user to home page
     */
    handleLogout () {
      localStorage.setItem('lang', this.selectedLanguage)
      this.$store.dispatch('logout')
      window.location.reload()
      this.$navigate('/login')
    },
    /**
     * Method to check user session if it exist
     * if it does not exist then redirect user to login page
     * @param sessionExist
     */
    async checkSession (sessionExist: boolean) {
      if (sessionExist) {
        window.gigya.socialize.getUserInfo({ callback: this.updateUserInfo })
      } else {
        if (!this.$route.meta?.isUserVerification && this.$route.meta?.requireAuthentication) {
          this.$navigate('/')
        }
        this.dispatchAlerts().then(() => this.getAlertContent())
      }
      this.isLoaded = true
    },
    /**
     * Callback method on click of Profile link under top right user name
     * @param event
     */
    userEdit () {
      this.$navigate('/my-profile')
    },

    setMagentoStore (language: string) {
      const storeLanguage = language === 'en' ? 'en' : 'pl'
      localStorage.setItem('store', `LivePortal_PL_${storeLanguage}`)
      if (!(this.user.country == null || this.user.country === '') && !this.$store.state.auth.isLanguageLoaded) {
        this.$store.commit(AuthMutationTypes.SET_LANGUAGE_LOADED, true)
      }
    },

    async search (event: CustomEvent) {
      this.searchValue = event.detail
      await this.fetchSearching(this.searchValue)
      const catalogQuery = {
        ...this.queries,
        search: this.searchValue
      }
      this.$store.commit('catalog/SET_QUERIES', catalogQuery, { root: true })
      this.$store.commit(ArticlesMutationType.SET_SEARCH, this.searchValue, { root: true })
      this.$store.commit(FilesMutationType.SET_SEARCH, this.searchValue, { root: true })
    },

    async fetchSearching (dto: string): Promise<IFetchSearchResponse[] | undefined> {
      try {
        if (dto != null && dto !== '') {
          const result = [] as IFetchSearchResponse[]
          if (this.checkPermission('Catalogue', {}).length > 0) {
            result.push({ name: this.$t('Catalogue'), path: '/catalog/products' })
          }
          result.push({ name: this.$t('News and Articles'), path: '/news' })
          result.push({ name: this.$t('Marketing Materials'), path: '/marketing-materials' })
          this.searchValues = result
        } else {
          this.searchValues = new Array(0) as IFetchSearchResponse[]
        }
        return this.searchValues
      } catch (error) {
        console.error(error)
      }
    },

    onResizePortal ({ height }: { height: number }) {
      this.clientWidth = document.querySelector('body')?.clientWidth || height
    },

    async removeProductFromCart (productId: string) {
      if (this.$store.state.basket?.id && productId) {
        await this.$store.dispatch('basket/REMOVE_ITEM', {
          cart_id: this.$store.state.basket.id,
          cart_item_id: productId
        }, { root: true })
        await this.$store.dispatch('basket/FETCH_MIN_CART_QUERY_FOR_CATALOG', { cart_id: this.$store.state.basket.id }, { root: true })
      }
    },

    startTimer () {
      this.timeoutId = setTimeout(this.userLogout, 900000)
    },
    resetTimer () {
      clearTimeout(this.timeoutId)
      this.startTimer()
    },
    setupTimers () {
      if (this.isTimerSetup) {
        return
      }

      document.addEventListener('keypress', this.resetTimer, false)
      document.addEventListener('keydown', this.resetTimer, false)
      document.addEventListener('click', this.resetTimer, false)
      document.addEventListener('mousemove', this.resetTimer, false)
      document.addEventListener('mousedown', this.resetTimer, false)
      document.addEventListener('wheel', this.resetTimer, false)
      document.addEventListener('touchstart', this.resetTimer, false)
      document.addEventListener('touchmove', this.resetTimer, false)
      window.addEventListener('scroll', this.resetTimer, false)

      this.isTimerSetup = true
      this.startTimer()
    }
  }
})
